import React, {
  useState,
  useCallback,
  useEffect,
  useContext,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { DateTime } from "luxon";
import FullCalendar from "@fullcalendar/react";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon3";
import { capitalize, some, compact } from "lodash";
import { RootState } from "typedefs";
import { cls } from "app/utils";
import { useTranslation } from "react-i18next";
import { CalendarPreferences } from "app/models/CalendarPreferences";
import { formatFullWeekdayDateTime } from "app/utils/luxonFormats";
import { UserContext, RoleType } from "app/contexts/UserContext";
import { asyncSchedulingProfileGet, setSchedulingData } from "app/actions/user";
import config from "config/environment";
import useFacilityLessonRates from "app/routes/Sessions/hooks/useFacilityLessonRates";

import { useEventsModel } from "./indexModel";
import { resolveSelectionDefaults } from "./selectionDefaults";
import useAvailability from "./hooks/useAvailability";
import useFacilityResources from "./hooks/useFacilityResources";
import { buildBayColumns } from "./resourceColumns";
import useConfirmation from "app/routes/Calendar/hooks/useConfirmation";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import useSlotInterval from "app/utils/hooks/scheduling/useSlotInterval";
import { canOpenLessonOnBay } from "./bayClick";
import { followingEditPayload } from "app/routes/Calendar/EventForm/seriesBay";

import {
  setSelectedEvent,
  deleteEvent,
  initializeNewEvent,
  createEvent,
  editEvent,
  startEdit,
  deselectEvent,
  hideInfoPopup,
  initializeCopyEvent,
} from "app/actions/events";
import {
  EventModel,
  EventType,
  bayIdFromColumnId,
  isBayColumnId,
} from "app/models/EventModel";
import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { AvailabilityModel } from "app/models/scheduling/AvailabilityModel";

import Event from "app/routes/Calendar/Event";
import EventInfo from "app/routes/Calendar/EventInfo";
import FacilityEventForm from "./EventForm";
import InfoModal from "app/routes/Calendar/InfoModal";
import ConfirmationPrompt from "app/routes/Calendar/ConfirmationPrompt";
import CustomRefundModal from "app/routes/Calendar/CustomRefundModal";
import { notifyStudentsFrom } from "app/routes/Calendar/hooks/notifyStudents";
import CoachInfo from "app/routes/Sessions/SessionForm/CoachInfo";
import ResourceLabel from "./ResourceLabel";
import CoachSettingsButton from "./CoachSettingsButton";
import CompareCoachHeader from "./CompareCoachHeader";

import styles from "./styles.module.scss";

const UNSELECT_CANCEL =
  ".event_form_container, .confirm_cancel, .confirm_cancel_conflict, .info-modal-content, .info-modal-header, .time-range-menu-dropdown, .invite_new_student_modal, .react-datepicker-popper, .rs__menu, [data-testid='compare-coach-header']";

interface FacilityCalendarViewProps {
  calendarRef: React.RefObject<any>;
  preferences: CalendarPreferences;
  eventElRef: React.RefObject<any>;
  initialDate: DateTime;
  onIntervalChange: (start: DateTime, end: DateTime) => void;
  initNewEventRef?: React.MutableRefObject<(() => void) | null>;
  // The Resources view draws one column per bay. The page owns both values and
  // the view only reads them.
  resourceView?: boolean;
  compareCoachId?: string | null;
  // The header of the coach column contains the coach selector. The page owns
  // the list and the choice, so the view sends every change to the page.
  compareCoaches?: CoachProfileModel[];
  onCompareCoachChange?: (coachId: string) => void;
}

export default function FacilityCalendarView({
  calendarRef,
  preferences,
  eventElRef,
  initialDate,
  onIntervalChange,
  initNewEventRef,
  resourceView = false,
  compareCoachId = null,
  compareCoaches = [],
  onCompareCoachChange,
}: FacilityCalendarViewProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const userId = useSelector((state: RootState) => state.user.self.id);
  const user = useContext(UserContext);

  const [calendarDate, setCalendarDate] = useState<DateTime>(
    initialDate || DateTime.now(),
  );
  // The Resources view draws a second grid for the coach. The two grids scroll
  // up and down together, so the view keeps a reference to each pane.
  const compareCalendarRef = useRef<any>(null);
  const comparePaneRef = useRef<HTMLDivElement>(null);
  const baysPaneRef = useRef<HTMLDivElement>(null);
  const scrollSyncRef = useRef<(() => void) | null>(null);
  const [selectedCoach, setSelectedCoach] = useState<CoachProfileModel>(null);
  const [withCoachSelection, setWithCoachSelection] = useState<boolean>(false);

  const facilityBookingEnabled =
    user.facility?.featureFlags?.facilityLevelBookingOptions === true;
  const { rates: facilityLessonRates } = useFacilityLessonRates(
    user.facility?.id,
    facilityBookingEnabled,
  );

  useEffect(() => {
    asyncSchedulingProfileGet()
      .then((facility) => dispatch(setSchedulingData(facility)))
      .catch((err) => console.error("Failed to refresh facility data", err));
    return () => {
      dispatch(deselectEvent());
    };
  }, []);

  const currentFacility = useCurrentFacility();
  const { step, snapDuration } = useSlotInterval();

  const timeZone = currentFacility.data?.timeZone || DateTime.now().zoneName;

  // Build resources from coach profiles
  let coachProfileResources = [];
  if (currentFacility.data?.id) {
    coachProfileResources = currentFacility.data.coachProfiles.map((coach) => ({
      id: coach.id,
      title: coach.fullName,
      profilePicture: coach.profilePicture,
      order: coach.orderNumber ?? Number.MAX_SAFE_INTEGER,
    }));
  }

  // Fetch facility events for the displayed day
  const { events } = useEventsModel(
    calendarDate?.startOf("day"),
    calendarDate?.endOf("day"),
  );

  // Fetch facility availability
  const { availability, loading: loadingAvailability } = useAvailability(
    calendarDate?.startOf("day"),
    calendarDate?.endOf("day"),
  );

  // Merge availability into resource businessHours
  if (availability.length > 0 && !loadingAvailability) {
    coachProfileResources = coachProfileResources.map((coach) => {
      const avs = availability.filter((av) => av.coachProfileId === coach.id);
      return { ...coach, businessHours: avs };
    });
  }

  // Only when the facility has the feature on: the resources tables ship in
  // the same release, and the deployer runs the migration after the deploy.
  const resourceManagementEnabled =
    currentFacility.data?.featureFlags?.resourceManagement === true;
  const showBayColumns = resourceView && resourceManagementEnabled;
  // A bay column needs about 148 px for a readable card (R9), so 7 bays fill
  // the width the frame shows. The Coaches view keeps the rule of develop: a
  // coach column takes 200 px once there are more than 3 coaches.
  const columnMinWidth = (count: number) => {
    if (showBayColumns) {
      return `${count * 148}px`;
    }
    return count > 3 ? `${count * 200}px` : undefined;
  };

  const { resources: scheduleResources } = useFacilityResources(
    currentFacility.data?.id,
    showBayColumns,
  );

  // The columns of the main grid. The Coaches view keeps one column per coach.
  // The Resources view replaces those columns with one column per bay.
  let columns = coachProfileResources;
  if (showBayColumns) {
    columns = buildBayColumns(scheduleResources);
  }

  // The compare coach gets a grid of its own, to the left of the bays. That
  // grid never scrolls sideways, so the coach column keeps its position.
  const compareCoach =
    showBayColumns && compareCoachId
      ? coachProfileResources.find((coach) => coach.id === compareCoachId)
      : undefined;
  const showCompareCoach = Boolean(compareCoach);
  const compareColumns = compareCoach ? [{ ...compareCoach, order: 0 }] : [];
  // A click on a bay column books the bay for the coach of the coach column.
  const compareCoachProfile: CoachProfileModel | undefined = compareCoach
    ? currentFacility.data?.coachProfiles?.find(
        (coach) => coach.id === compareCoach.id,
      )
    : undefined;

  // The booking options a new lesson of the coach can take.
  const lessonRatesFor = useCallback(
    (coach: CoachProfileModel | undefined) =>
      facilityBookingEnabled ? facilityLessonRates : coach?.lessonRates,
    [facilityBookingEnabled, facilityLessonRates],
  );

  // Tells if a click on the bay can open the lesson form.
  const bayClickAllowed = useCallback(
    (bayId: number) =>
      canOpenLessonOnBay({
        compareCoach: compareCoachProfile,
        user,
        lessonRates: lessonRatesFor(compareCoachProfile),
        bay: scheduleResources.find((resource) => resource.id === bayId),
      }),
    [compareCoachProfile, user, lessonRatesFor, scheduleResources],
  );

  const { PromptComponent, requestConfirmation } = useConfirmation(
    (props: any) => <ConfirmationPrompt {...props} />,
  );
  const {
    PromptComponent: RefundPromptComponent,
    requestConfirmation: refundRequestConfirmation,
  } = useConfirmation((props: any) => <CustomRefundModal {...props} />);

  const selectedEvent: EventModel = useSelector(
    (state: RootState) => state.events.selected,
  );
  const isEditingEvent: boolean = useSelector(
    (state: RootState) => state.events.editSelected,
  );
  const newEvent = useSelector((state: RootState) => state.events.newEvent);
  const showInfoPopup = useSelector(
    (state: RootState) => state.events.showInfoPopup,
  );
  const infoPopupContent = useSelector(
    (state: RootState) => state.events.infoPopupContent,
  );

  const eventForForm = selectedEvent || newEvent;
  const showEventForm = (selectedEvent && isEditingEvent) || newEvent;
  const eventSubmitErrors = events.error;

  const onDeleteEvent = useCallback(() => {
    const isNonRefundable = selectedEvent.refundable === false;
    const isOnline = selectedEvent.payment_method === "online";
    const isPackage = selectedEvent.payment_method === "package";

    let content =
      selectedEvent.type === "coach_schedule_block"
        ? t("Please confirm that you would like to cancel this event.")
        : t("Please confirm that you would like to delete this session.");

    if (selectedEvent.recurrenceGroupId) {
      content =
        selectedEvent.type === "coach_schedule_block"
          ? t(
              "Your event is set to {{repeat}}. You can choose whether you wish to apply these changes to the repeating dates as well.",
              { repeat: capitalize(selectedEvent.repeat) },
            )
          : t(
              'Your session is set to "Repeating - {{repeat}}". You can choose whether you wish to apply these changes to the repeating dates as well.',
              { repeat: capitalize(selectedEvent.repeat) },
            );
    }

    const title =
      selectedEvent.type === "coach_schedule_block"
        ? t("Delete Unavailability")
        : t("Cancel Session");

    const showRefundModal = !isNonRefundable && (isOnline || isPackage);
    const confirmationFn = showRefundModal
      ? refundRequestConfirmation
      : requestConfirmation;

    confirmationFn({
      event: selectedEvent,
      content,
      title,
      operation: "delete",
      actions: {
        onConfirm: (_data, options) =>
          dispatch(deleteEvent(selectedEvent, undefined, false, notifyStudentsFrom(options))),
        onConfirmFollowing: (_data, options) =>
          dispatch(deleteEvent(selectedEvent, "following", false, notifyStudentsFrom(options))),
        onChargeLateCancellation: (_data, options) =>
          dispatch(deleteEvent(selectedEvent, undefined, true, notifyStudentsFrom(options))),
      },
    });
  }, [
    selectedEvent,
    requestConfirmation,
    refundRequestConfirmation,
    dispatch,
    t,
  ]);

  const onSaveEventForm = useCallback(
    (data: any) => {
      if (data.id) {
        const payload = EventModel.apiPayload(data, "update");
        let content = t(
          "Please confirm that you would like to edit this session.",
        );
        if (data.recurrenceGroupId) {
          content = t(
            "Your session is set to \u201CRepeating - {{repeat}}\u201D. You can choose whether you wish to apply these changes to the repeating dates as well.",
            { repeat: capitalize(selectedEvent.repeat) },
          );
        }
        requestConfirmation({
          event: selectedEvent,
          content,
          title: t("Edit Event"),
          operation: "edit",
          actions: {
            onConfirm: () => dispatch(editEvent(payload)),
            // A series edit sends the bay only when the user picked one or
            // the bay changed. See bayKeyForScope.
            onConfirmFollowing: () =>
              dispatch(
                editEvent(
                  followingEditPayload(payload, data, selectedEvent?.scheduleResourceId),
                ),
              ),
          },
        });
      } else {
        const payload = EventModel.apiPayload(data, "create");
        dispatch(createEvent(payload));
      }
    },
    [selectedEvent, requestConfirmation, dispatch, t],
  );

  const onEditEvent = useCallback(() => {
    dispatch(startEdit());
  }, [dispatch]);

  const onCopyEvent = useCallback(() => {
    const coach = currentFacility.data?.coachProfiles?.find(
      (c) => c.id === selectedEvent.coachProfile?.id,
    );
    setSelectedCoach(coach || selectedEvent.coachProfile);
    setWithCoachSelection(true);
    dispatch(initializeCopyEvent(selectedEvent));
  }, [currentFacility.data, selectedEvent, dispatch]);

  const onCancelSelection = useCallback(() => {
    calendarRef.current?.getApi()?.unselect();
    compareCalendarRef.current?.getApi()?.unselect();
    setSelectedCoach(null);
    setWithCoachSelection(false);
    dispatch(deselectEvent());
  }, [calendarRef, dispatch]);

  const initNewEvent = useCallback(() => {
    setWithCoachSelection(true);
    dispatch(
      initializeNewEvent({
        start: null,
        end: null,
        date: calendarDate.startOf("day"),
        type: EventType.LESSON_SCHEDULE,
        timeZone,
        backgroundColor: "#0279b3",
        coachProfiles: [],
      }),
    );
  }, [dispatch, calendarDate, timeZone]);

  useEffect(() => {
    if (!initNewEventRef) {
      return undefined;
    }
    initNewEventRef.current = initNewEvent;
    return () => {
      initNewEventRef.current = null;
    };
  }, [initNewEvent, initNewEventRef]);

  const selectAllow = useCallback(
    (info: any) => {
      // A bay column takes a click only while the coach column shows a coach.
      // The new lesson goes to that coach, so the rules of that coach apply.
      const bayId = bayIdFromColumnId(info.resource.id);
      if (bayId !== null && !bayClickAllowed(bayId)) {
        return false;
      }
      const coach =
        bayId !== null
          ? compareCoachProfile
          : currentFacility.data?.coachProfiles?.find(
              (c) => c.id === info.resource.id,
            );
      if (!coach) {
        return false;
      }

      const localStart = DateTime.fromJSDate(info.start).setZone(
        coach.timeZone,
      );
      const localEnd = DateTime.fromJSDate(info.end).setZone(coach.timeZone);

      if (
        (user.role === RoleType.COACH && user.isAdmin) ||
        user.role === RoleType.ADMIN
      ) {
        return true;
      }

      if (user.role === RoleType.COACH && userId === coach.id) {
        return true;
      }

      if (info.start.getTime() - Date.now() < 0) {
        return false;
      }

      if (
        DateTime.fromJSDate(info.end, { zone: timeZone })
          .diff(coach.latestBookingDate)
          .toMillis() > 0
      ) {
        return false;
      }

      if (
        DateTime.fromJSDate(info.start, { zone: timeZone })
          .diff(coach.earliestBookingDate)
          .toMillis() < 0
      ) {
        return false;
      }

      const avs = availability.filter((av) => av.coachProfileId === coach.id);
      const businessHourIntervals = AvailabilityModel.getIntervals(avs);
      const withinHours = some(
        businessHourIntervals,
        (interval) =>
          interval.contains(localStart) &&
          interval.contains(localEnd.minus({ millisecond: 1 })),
      );

      return withinHours;
    },
    [
      currentFacility.data,
      user,
      userId,
      timeZone,
      availability,
      compareCoachProfile,
      bayClickAllowed,
    ],
  );

  const handleSelect = useCallback(
    (info: any) => {
      const bayId = bayIdFromColumnId(info.resource.id);
      if (isBayColumnId(info.resource.id) && bayId === null) {
        return;
      }
      if (bayId !== null && !bayClickAllowed(bayId)) {
        return;
      }

      const tz = info.view.dateEnv.timeZone;
      const startDt = DateTime.fromISO(info.startStr).setZone(tz);
      const coach =
        bayId !== null
          ? compareCoachProfile
          : currentFacility.data?.coachProfiles?.find(
              (c) => c.id === info.resource.id,
            );
      // A click selects one snap interval, not the length of a booking option —
      // stretch it to the default option's duration (CN-2797). A click on a
      // bay takes the option for that bay.
      const { end, lessonRate, lessonRateId } = resolveSelectionDefaults({
        startStr: info.startStr,
        endStr: info.endStr,
        timeZone: tz,
        lessonRates: lessonRatesFor(coach),
        snapInMinutes: step,
        bayId,
        resources: scheduleResources,
      });

      setSelectedCoach(coach);
      // The form shows the coach selector after every click, so the user can
      // give the lesson to another coach.
      setWithCoachSelection(true);

      dispatch(
        initializeNewEvent({
          backgroundColor: "#0279b3",
          date: startDt.startOf("day"),
          start: info.startStr,
          end,
          type: EventType.LESSON_SCHEDULE,
          coachProfiles: [coach],
          lessonRateId,
          rate: lessonRate?.rate,
          location: lessonRate?.location,
          timeZone: tz,
          scheduleResourceId: bayId,
          clickedBayId: bayId,
        }),
      );
    },
    [
      dispatch,
      currentFacility.data,
      compareCoachProfile,
      bayClickAllowed,
      lessonRatesFor,
      scheduleResources,
      step,
    ],
  );

  const handleEventClick = useCallback(
    (info: any) => {
      (eventElRef as React.MutableRefObject<any>).current = info.el;
      dispatch(setSelectedEvent(parseInt(info.event.id, 10)));
    },
    [dispatch, eventElRef],
  );

  const handleDatesSet = useCallback(
    (dateInfo: any) => {
      const start = DateTime.fromJSDate(dateInfo.start).setZone(timeZone);
      const end = DateTime.fromJSDate(dateInfo.end).setZone(timeZone);
      setCalendarDate(start);
      onIntervalChange(start, end);
    },
    [timeZone, onIntervalChange],
  );

  // The facility calendar uses the time zone of the facility. Without this
  // prop the label of an event formats in the zone of the device, and it shows
  // an hour that the grid does not have.
  const renderEvent = useCallback(
    (info: any) => (
      <Event
        {...info}
        timeZone={timeZone}
        facilityResourceView={showBayColumns}
      />
    ),
    [showBayColumns, timeZone],
  );

  // The coach grid shows the bay of a session, because the column already
  // shows the coach. The Coaches view does the same.
  const renderCompareEvent = useCallback(
    (info: any) => (
      <Event {...info} timeZone={timeZone} facilityResourceView={false} />
    ),
    [timeZone],
  );

  const showCoachSettingsGear =
    user.behavesLikeAdmin &&
    currentFacility.data?.featureFlags?.adminsCanEditCoachSettings;

  const handleGearClick = useCallback(
    (coachId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      sessionStorage.setItem("calendarSettings_selectedCoachId", coachId);
      history.push("/calendar/pricing", { fromCalendarSettings: true });
    },
    [history],
  );

  const renderResourceLabelContent = useCallback(
    (info: any) => {
      if (isBayColumnId(info.resource.id)) {
        return (
          <ResourceLabel
            title={info.resource.title}
            section={info.resource.extendedProps.section}
          />
        );
      }

      if (!currentFacility.data?.coachProfiles) {
        return null;
      }
      const coach = currentFacility.data.coachProfiles.find(
        (c) => c.id === info.resource.id,
      );
      if (!coach) {
        return null;
      }

      const gearIcon = showCoachSettingsGear ? (
        <CoachSettingsButton coachId={coach.id} onClick={handleGearClick} />
      ) : null;

      return (
        <CoachInfo coach={coach} singleCoach={false} nameSuffix={gearIcon} />
      );
    },
    [currentFacility.data, showCoachSettingsGear, handleGearClick],
  );

  const renderCompareCoachLabel = useCallback(
    () => (
      <CompareCoachHeader
        coaches={compareCoaches}
        selectedCoachId={compareCoachId}
        onChange={(coachId: string) => onCompareCoachChange?.(coachId)}
        onSettingsClick={showCoachSettingsGear ? handleGearClick : undefined}
      />
    ),
    [
      compareCoaches,
      compareCoachId,
      onCompareCoachChange,
      showCoachSettingsGear,
      handleGearClick,
    ],
  );

  // The coach grid follows the date of the main grid. The toolbar changes the
  // date of the main grid only.
  useEffect(() => {
    if (!showCompareCoach || !calendarDate) {
      return;
    }
    compareCalendarRef.current?.getApi()?.gotoDate(calendarDate.toJSDate());
  }, [showCompareCoach, calendarDate]);

  const detachScrollSync = useCallback(() => {
    scrollSyncRef.current?.();
    scrollSyncRef.current = null;
  }, []);

  // The two grids scroll up and down together. Each one writes the position of
  // the other, and only on a real difference, so a scroll event never repeats.
  // Each grid calls this function when FullCalendar mounts its view, so the
  // pair works from the first paint.
  const attachScrollSync = useCallback(() => {
    detachScrollSync();

    const bodyScroller = (pane: HTMLDivElement | null) =>
      pane?.querySelector<HTMLElement>(
        ".fc-scrollgrid-section-body .fc-scroller",
      ) ?? null;

    const coachScroller = bodyScroller(comparePaneRef.current);
    const baysScroller = bodyScroller(baysPaneRef.current);
    if (!coachScroller || !baysScroller) {
      return;
    }

    const follow = (from: HTMLElement, to: HTMLElement) => () => {
      if (to.scrollTop !== from.scrollTop) {
        to.scrollTop = from.scrollTop;
      }
    };

    const coachFollowsBays = follow(baysScroller, coachScroller);
    const baysFollowCoach = follow(coachScroller, baysScroller);
    baysScroller.addEventListener("scroll", coachFollowsBays);
    coachScroller.addEventListener("scroll", baysFollowCoach);
    coachScroller.scrollTop = baysScroller.scrollTop;

    scrollSyncRef.current = () => {
      baysScroller.removeEventListener("scroll", coachFollowsBays);
      coachScroller.removeEventListener("scroll", baysFollowCoach);
    };
  }, [detachScrollSync]);

  // The mount callback of the first grid finds one scroller only. This effect
  // runs after both grids mount and attaches the pair. It also removes the
  // listeners when the view drops the coach grid, and when the page unmounts.
  useEffect(() => {
    attachScrollSync();
    return detachScrollSync;
  }, [attachScrollSync, detachScrollSync, showCompareCoach, compareCoachId]);

  const coachForForm = isEditingEvent
    ? currentFacility.data?.coachProfiles?.find(
        (cp: any) => cp.id === selectedEvent?.coachProfile?.id,
      ) || selectedEvent?.coachProfile
    : selectedCoach;

  // Both grids take the same options, so the rows of the coach match the rows
  // of the bays.
  const sharedCalendarProps = {
    schedulerLicenseKey: config.FULL_CALENDAR_LICENSE,
    headerToolbar: false as const,
    events: events.data as any[],
    allDaySlot: false,
    selectable: true,
    selectMirror: true,
    unselectAuto: true,
    expandRows: true,
    unselectCancel: UNSELECT_CANCEL,
    plugins: [resourceTimeGridPlugin, luxonPlugin, interactionPlugin],
    unselect: () => dispatch(deselectEvent()),
    select: handleSelect,
    selectAllow,
    dayHeaderFormat: {
      day: "numeric" as const,
      weekday: "short" as const,
    },
    slotLabelFormat: {
      hour: "numeric" as const,
      minute: "2-digit" as const,
      omitZeroMinute: true,
      hour12: preferences.timeFormat === "12h",
    },
    eventTimeFormat: {
      hour: "numeric" as const,
      minute: "2-digit" as const,
      omitZeroMinute: true,
      hour12: preferences.timeFormat === "12h",
    },
    initialView: "resourceTimeGridDay",
    resourceOrder: "order,title",
    timeZone,
    businessHours: availability,
    slotDuration: "01:00:00",
    height: "100%",
    eventBackgroundColor: "#039BE5",
    snapDuration,
    eventClick: handleEventClick,
    longPressDelay: 600,
    scrollTime: "05:45:00",
    nowIndicator: true,
    nowIndicatorClassNames: "facility-calendar-now-indicator-line",
    firstDay: preferences.firstDay,
    viewDidMount: attachScrollSync,
  };

  return (
    <>
      {showEventForm && (
        <FacilityEventForm
          event={eventForForm}
          onCancel={onCancelSelection}
          onSave={onSaveEventForm}
          submitting={events.pendingCreate || events.pendingEdit}
          errors={eventSubmitErrors}
          coachProfiles={compact([coachForForm])}
          coachProfileOptions={currentFacility.data?.coachProfiles}
          allowCoachSelection={withCoachSelection}
          facility={currentFacility.data}
          facilityLessonRates={facilityLessonRates}
          facilityBookingEnabled={facilityBookingEnabled}
          bayView={showBayColumns}
        />
      )}

      <InfoModal
        isOpen={showInfoPopup}
        title={infoPopupContent.title}
        status={infoPopupContent.status}
        onDismiss={() => dispatch(hideInfoPopup())}
        payload={infoPopupContent.payload}
        action={infoPopupContent.action}
        canOverride={infoPopupContent.canOverride}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div>{infoPopupContent.content}</div>
          {infoPopupContent.item && (
            <div>
              <b style={{ color: "#000" }}>
                {formatFullWeekdayDateTime(
                  DateTime.fromISO(infoPopupContent.item?.start, {
                    zone: timeZone,
                  }),
                  preferences.dateFormat,
                  preferences.timeFormat,
                )}
              </b>
            </div>
          )}
        </div>
      </InfoModal>

      <PromptComponent />
      <RefundPromptComponent />

      <EventInfo
        timeZone={timeZone}
        event={selectedEvent}
        referenceElement={eventElRef.current}
        onDelete={onDeleteEvent}
        onEdit={onEditEvent}
        onCopy={onCopyEvent}
      />

      <div
        className={cls(styles.facility_calendar_layout, {
          [styles.with_compare_coach]: showCompareCoach,
        })}
      >
        {showCompareCoach && (
          <div
            className={styles.compare_coach_pane}
            ref={comparePaneRef}
            data-testid="compare-coach-pane"
          >
            <FullCalendar
              {...sharedCalendarProps}
              ref={compareCalendarRef}
              resources={compareColumns}
              initialDate={calendarDate?.toJSDate()}
              eventContent={renderCompareEvent}
              resourceLabelContent={renderCompareCoachLabel}
            />
          </div>
        )}

        <div
          className={styles.facility_calendar_scroll_wrapper}
          ref={baysPaneRef}
        >
          <div
            className={styles.facility_calendar_inner}
            style={{ minWidth: columnMinWidth(columns.length) }}
          >
            <FullCalendar
              {...sharedCalendarProps}
              ref={calendarRef}
              resources={columns}
              initialDate={initialDate?.toJSDate()}
              datesSet={handleDatesSet}
              eventContent={renderEvent}
              resourceLabelContent={renderResourceLabelContent}
            />
          </div>
        </div>
      </div>

    </>
  );
}
